import React, { useCallback, useEffect, useId, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MdAccessTime,
  MdChatBubbleOutline,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdTrackChanges,
} from "react-icons/md";
import { getApiErrorMessage } from "api";
import Button from "components/button";
import { Textarea } from "components/fields";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import notify from "lib/notify";
import { formatDate } from "utils/format";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import RatingScale from "views/public/shared/RatingScale";
import SurveyProgress from "views/public/shared/SurveyProgress";
import { PUBLIC_AUDIENCES } from "views/public/shared/audiences";
import { isRetryableError } from "views/public/shared/errors";

const HIGHLIGHTS = [
  { icon: MdChatBubbleOutline, title: "Share Stories", description: "Moments from Sunday or Wednesday" },
  { icon: MdTrackChanges, title: "Your Focus", description: "What you worked on this week" },
  { icon: MdAccessTime, title: "About 3 Minutes", description: "Quick and easy to complete" },
];

const currentWeekLabel = () => {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  return `Week of ${formatDate(monday)}`;
};

const PulseQuestion = ({ question, value, onChange, onNext, onBack, isLast, submitting }) => {
  const headingId = useId();
  const isText = question.type === "text";
  const maxLength = question.maxLength || 500;
  const canContinue = isText ? Boolean(value && value.trim()) : Boolean(value);

  return (
    <>
      <h1 id={headingId} className="mb-6 text-heading leading-relaxed">
        {question.question}
      </h1>

      {isText ? (
        <Textarea
          aria-labelledby={headingId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          maxLength={maxLength}
          rows={5}
          hint={`${(value || "").length}/${maxLength} characters`}
        />
      ) : (
        <RatingScale
          value={value || null}
          onChange={onChange}
          labelledBy={headingId}
          lowLabel={question.lowLabel}
          highLabel={question.highLabel}
        />
      )}

      <div className="mt-8 flex gap-3">
        <Button
          variant="secondary"
          size="lg"
          leftIcon={<MdChevronLeft aria-hidden="true" className="h-5 w-5" />}
          onClick={onBack}
          disabled={submitting}
          className="flex-1"
        >
          Back
        </Button>
        <Button
          size="lg"
          rightIcon={isLast ? null : <MdChevronRight aria-hidden="true" className="h-5 w-5" />}
          onClick={onNext}
          disabled={!canContinue}
          loading={submitting}
          className="flex-1"
        >
          {isLast ? (submitting ? "Submitting…" : "Submit") : "Next"}
        </Button>
      </div>
    </>
  );
};

/**
 * Weekly pulse check-in opened from an emailed/SMS link: /staff-pulse/:token and
 * /volunteer-pulse/:token. Questions come from the API.
 */
const PulsePage = ({ audience }) => {
  const { service } = PUBLIC_AUDIENCES[audience];
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pulseData, setPulseData] = useState(null);
  const [phase, setPhase] = useState("intro"); // intro | questions | complete
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const weekLabel = currentWeekLabel();
  const orgName = pulseData?.organization?.name;

  const fetchSurvey = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await service.getPublicPulse(token);
      if (response.success) {
        setPulseData(response.data);
      } else {
        setError({ message: response.message || "Unable to load survey.", retryable: false });
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setError({ message: "This survey link is invalid or has expired.", retryable: false });
      } else if (status === 400) {
        setError({
          message: err.response?.data?.error || "This survey has already been completed.",
          retryable: false,
        });
      } else {
        setError({
          message: "Unable to load survey. Please try again later.",
          retryable: isRetryableError(err),
        });
      }
    } finally {
      setLoading(false);
    }
  }, [service, token]);

  useEffect(() => {
    if (token) fetchSurvey();
  }, [token, fetchSurvey]);

  const questions = pulseData?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const isLast = currentQuestionIndex === questions.length - 1;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const responsePayload = questions.map((q) => ({
        questionId: q.id,
        question: q.question,
        value: answers[q.id] ?? null,
      }));

      const textAnswers = questions
        .filter((q) => q.type === "text")
        .map((q) => answers[q.id])
        .filter(Boolean)
        .join("\n\n");

      await service.submitPulse(token, {
        responses: responsePayload,
        openEndedResponse: textAnswers || null,
      });

      setPhase("complete");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Couldn't submit your check-in. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (submitting) return;
    if (isLast) {
      handleSubmit();
    } else {
      setCurrentQuestionIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((i) => i - 1);
    } else {
      setPhase("intro");
    }
  };

  if (loading) {
    return (
      <PublicSurveyShell center>
        <Loader size="lg" label="Loading check-in" />
      </PublicSurveyShell>
    );
  }

  if (error) {
    return (
      <PublicSurveyShell center>
        <ErrorState
          title="Check-In Unavailable"
          titleAs="h1"
          message={error.message}
          onRetry={error.retryable ? fetchSurvey : undefined}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "complete") {
    return (
      <PublicSurveyShell organizationName={orgName} center>
        <div className="text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-subtle text-success-fg">
            <MdCheckCircle aria-hidden="true" className="h-8 w-8" />
          </span>
          <h1 className="mt-4 text-title">Check-In Complete</h1>
          <p className="mt-1 text-sm text-fg-muted">{weekLabel}</p>
          <p className="mt-6 text-sm leading-relaxed text-fg">
            Thank you for taking time to share your week. Your input helps us celebrate wins,
            address blockers, and stay aligned as a team.
          </p>
          <p className="mt-3 text-sm text-fg-muted">
            Your responses have been recorded and will be included in this week's dashboard summary.
          </p>
        </div>
      </PublicSurveyShell>
    );
  }

  if (phase === "intro") {
    return (
      <PublicSurveyShell organizationName={orgName} center>
        <div className="text-center">
          <h1 className="text-title">Weekly Check-In</h1>
          <p className="mt-1 text-sm text-fg-muted">{weekLabel}</p>
        </div>

        <h2 className="mt-8 text-heading">Quick 5 Questions</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Help us understand how your week went and celebrate the stories from our congregation.
        </p>
        <ul className="mt-4 flex flex-col gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
            <li key={title} className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                <Icon aria-hidden="true" className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-medium text-fg">{title}</p>
                <p className="text-xs text-fg-muted">{description}</p>
              </div>
            </li>
          ))}
        </ul>

        <Button size="lg" fullWidth className="mt-8" onClick={() => setPhase("questions")}>
          Start Check-In
        </Button>
        <p className="mt-4 text-center text-xs text-fg-muted">
          Your responses help leadership understand team health and celebrate wins together.
        </p>
      </PublicSurveyShell>
    );
  }

  if (!currentQuestion) {
    return (
      <PublicSurveyShell organizationName={orgName} center>
        <Loader size="lg" label="Loading questions" />
      </PublicSurveyShell>
    );
  }

  return (
    <PublicSurveyShell
      organizationName={orgName}
      progress={<SurveyProgress current={currentQuestionIndex + 1} total={questions.length} />}
    >
      <PulseQuestion
        key={currentQuestion.id}
        question={currentQuestion}
        value={answers[currentQuestion.id]}
        onChange={(val) => setAnswers((prev) => ({ ...prev, [currentQuestion.id]: val }))}
        onNext={handleNext}
        onBack={handleBack}
        isLast={isLast}
        submitting={submitting}
      />
    </PublicSurveyShell>
  );
};

export default PulsePage;
